import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import {
  Eye, MessagesSquare, MousePointerClick, CheckCircle2,
  TrendingUp, Loader2, ChevronDown,
} from "lucide-react";

const PERIODS = [
  { id: "7d", label: "7 giorni" },
  { id: "30d", label: "30 giorni" },
  { id: "90d", label: "90 giorni" },
  { id: "all", label: "Sempre" },
];

const STORAGE_KEY = "admin_analytics_open";

export default function WebsiteAnalyticsCard() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  // Collapsed by default — user can expand to view full website tracking.
  const [open, setOpen] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get(`/admin/analytics/summary?period=${period}`),
      api.get(`/admin/analytics/timeline?days=${period === "7d" ? 7 : period === "90d" ? 30 : 14}`),
    ])
      .then(([s, t]) => { if (!mounted) return; setData(s.data); setTimeline(t.data.timeline || []); })
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [period, open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  };

  return (
    <Card className="p-5 bg-white border border-[#E8E2D9]" data-testid="website-analytics-card">
      <button
        type="button"
        onClick={toggle}
        data-testid="analytics-toggle"
        className="w-full flex items-start justify-between gap-3 flex-wrap text-left"
        aria-expanded={open}
      >
        <div className="flex items-start gap-3">
          <ChevronDown
            className={`w-5 h-5 mt-1 text-[#635F59] transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
          />
          <div>
            <h3 className="font-display text-xl text-[#1E3A5F]">Tracciamento sito web</h3>
            <p className="text-xs text-[#635F59] mt-0.5">
              {open
                ? "Quanti visitano, chattano e prenotano dal tuo sito"
                : "Tocca per vedere visitatori, chat e prenotazioni"}
            </p>
          </div>
        </div>
        {open && (
          <div
            className="flex gap-1 bg-[#F5F1EA] p-1 rounded-lg"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={(e) => { e.stopPropagation(); setPeriod(p.id); }}
                data-testid={`analytics-period-${p.id}`}
                className={`px-3 py-1 text-xs rounded-md transition ${
                  period === p.id ? "bg-white text-[#1E3A5F] shadow font-semibold" : "text-[#635F59] hover:text-[#1E3A5F]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </button>

      {open && (
        <div className="mt-4">
          {loading ? (
            <div className="py-10 text-center text-[#635F59]">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Caricamento metriche...
            </div>
          ) : !data ? (
            <div className="py-8 text-center text-[#635F59] text-sm">
              Nessun dato disponibile. Le metriche inizieranno a popolarsi appena i clienti visiteranno il sito.
            </div>
          ) : (
            <>
          {/* 4 main KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KpiCard
              icon={Eye}
              label="Visitatori unici"
              value={data.unique_visitors}
              sub={`${data.events.page_views} visualizzazioni pagina`}
              color="blue"
              testId="kpi-visitors"
            />
            <KpiCard
              icon={MessagesSquare}
              label="Hanno chattato"
              value={data.unique_chat_users}
              sub={`${data.conversion.visit_to_chat_pct}% dei visitatori`}
              color="orange"
              testId="kpi-chat"
            />
            <KpiCard
              icon={MousePointerClick}
              label="Iniziato prenotazione"
              value={data.unique_booking_starts}
              sub={`${data.conversion.visit_to_booking_start_pct}% dei visitatori`}
              color="amber"
              testId="kpi-booking-start"
            />
            <KpiCard
              icon={CheckCircle2}
              label="Hanno prenotato"
              value={data.unique_booking_done}
              sub={`${data.conversion.visit_to_booking_done_pct}% dei visitatori`}
              color="green"
              testId="kpi-booking-done"
            />
          </div>

          {/* Conversion funnel mini */}
          <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 mb-4" data-testid="conversion-funnel">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[#AC5D45]" />
              <h4 className="font-semibold text-[#1E3A5F] text-sm">Imbuto di conversione</h4>
            </div>
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              <FunnelStep label="Visita → Chat" pct={data.conversion.visit_to_chat_pct} />
              <FunnelStep label="Visita → Prenota" pct={data.conversion.visit_to_booking_done_pct} highlight />
              <FunnelStep label="Chat → Prenota" pct={data.conversion.chat_to_booking_pct} />
            </div>
          </div>

          {/* Mini bar chart (timeline) */}
          {timeline.length > 0 && (
            <div data-testid="analytics-timeline">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs uppercase tracking-wider text-[#635F59]">Andamento giornaliero</h4>
                <div className="text-[10px] text-[#635F59] flex gap-2.5">
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-[#1E3A5F] rounded-sm"></span>Visite</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-[#AC5D45] rounded-sm"></span>Chat</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-emerald-500 rounded-sm"></span>Prenotazioni</span>
                </div>
              </div>
              <TimelineChart timeline={timeline} />
            </div>
          )}
        </>
      )}
        </div>
      )}
    </Card>
  );
}

const COLOR_MAP = {
  blue: { bg: "bg-[#E8EEF6]", text: "text-[#1E3A5F]" },
  orange: { bg: "bg-[#FAEAE3]", text: "text-[#AC5D45]" },
  amber: { bg: "bg-amber-50", text: "text-amber-700" },
  green: { bg: "bg-emerald-50", text: "text-emerald-700" },
};

function KpiCard({ icon: Icon, label, value, sub, color, testId }) {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  return (
    <div className="border border-[#E8E2D9] rounded-lg p-3 bg-white" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className={`w-8 h-8 rounded-full ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${c.text}`} />
        </div>
      </div>
      <div className="font-display text-3xl text-[#1E3A5F] leading-none mb-1">{value}</div>
      <div className="text-xs font-semibold text-[#2D2926]">{label}</div>
      <div className="text-[10px] text-[#635F59] mt-0.5">{sub}</div>
    </div>
  );
}

function FunnelStep({ label, pct, highlight }) {
  return (
    <div className={`rounded-md px-2.5 py-2 ${highlight ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-[#E8E2D9]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#635F59]">{label}</div>
      <div className={`font-display text-xl ${highlight ? "text-emerald-700" : "text-[#1E3A5F]"}`}>{pct}%</div>
    </div>
  );
}

function TimelineChart({ timeline }) {
  const maxV = Math.max(1, ...timeline.flatMap((d) => [d.page_view, d.chat_open, d.booking_completed]));
  return (
    <div className="flex items-end gap-1 h-24">
      {timeline.map((d) => {
        const h1 = (d.page_view / maxV) * 100;
        const h2 = (d.chat_open / maxV) * 100;
        const h3 = (d.booking_completed / maxV) * 100;
        return (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-0.5 min-w-[6px]">
            <div className="flex-1 w-full flex items-end gap-px" title={`${d.day} · ${d.page_view} visite · ${d.chat_open} chat · ${d.booking_completed} prenotazioni`}>
              <div className="flex-1 bg-[#1E3A5F] rounded-sm" style={{ height: `${h1}%` }} />
              <div className="flex-1 bg-[#AC5D45] rounded-sm" style={{ height: `${h2}%` }} />
              <div className="flex-1 bg-emerald-500 rounded-sm" style={{ height: `${h3}%` }} />
            </div>
            <div className="text-[8px] text-[#635F59] truncate w-full text-center">{d.day.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}
